import { generateOptions } from "utils/miscUtils";

class SessionHelper {
  email: any = null;
  role: any = null;
  token: any = null;
  countries: any = null;
  countryOptions: any = null;
  countryNameOptions: any = null;
  ovrFormulas: any = null;
  languages: any = null;
  languageOptions: any = null;
  currencies: any[] = [];
  currencyOptions: any[] = [];
  clubs: any[] = [];
  cardBgs: any[] = [];
  clubsOptions: any[] = [];
  positionsOptions: any[] = [];
  positions: any[] = [];
  allowedPlatforms: any[] = [];
  allowedOperators: any[] = [];
  allowedBrands: any[] = [];
  platformOptions: any[] = [];
  operatorOptions: any[] = [];
  brandOptions: any[] = [];
  rewardPacks: any[] = []; // Add this line to store reward packs
  rewardPackOptions: any[] = []; // Add this line to store reward pack options
  availableCurrencies = ["INR", "EUR", "USD", "GBP"];
  transactionStatusOptions = ["BET_PLACED", "COIN_PURCHASED"];
  marketTypeOptions = [
    "FINAL_RESULT",
    "UNDER_OVER",
    "BTTS",
    "RESULT_BTTS",
    "CORRECT_SCORE",
    "FIRST_LAST_ANY_GOAL_SCORE",
    "HANDICAPS",
    "FIRST_GOAL_CORRECT_SCORE",
  ];
  betStatusOptions = [
    "debit_completed",
    "credit_started",
    "credit_completed",
    "refund_completed",
    "debit_failed",
    "credit_failed",
    "refund_failed",
  ];

  onSignIn({ email, name, role, sid, isActive, id, lastLogin, requiresPasswordReset }: any) {
    this.email = email;
    this.role = role;
    this.token = sid;

    localStorage.setItem(
      "userInfo",
      JSON.stringify({
        token: sid,
        email,
        role,
        name,
        isActive,
        id,
        lastLogin,
        requiresPasswordReset,
      })
    );
  }

  onCreateSession(details: any) {
    const {
      countries,
      ovrFormula,
      platforms,
      operators,
      brands,
      cardBgs,
      clubs,
      positions,
      languages,
      currencies,
      rewardPacks, // Add this to destructure rewardPacks from details
    } = details;

    this.countries = countries;
    this.countryOptions = [
      { id: "all", label: "All" },
      ...countries?.map((country: any) => ({
        id: country?.isoCode,
        label: country?.name,
      })),
    ];

    this.countryNameOptions = [
      { id: "all", label: "All" },
      ...countries?.map((country: any) => ({
        id: country?.name,
        label: country?.name,
      })),
    ];

    this.currencies = currencies;
    this.ovrFormulas = ovrFormula;
    this.languages = languages;
    this.languageOptions = languages?.map((item: any) => ({
      id: item.isoCode,
      label: item.name,
    }));

    this.clubs = clubs || [];
    this.cardBgs = cardBgs || [];
    this.positions = positions || [];

    this.clubsOptions = clubs.map((cl: any) => ({
      id: cl.externalClubId,
      label: `${cl.name} - ${cl.abbreviation}`,
    }));
    this.positionsOptions = generateOptions({
      items: positions,
      id: "code",
      label: "code",
    });

    this.allowedPlatforms = platforms;
    this.allowedOperators = operators;
    this.allowedBrands = brands;

    this.platformOptions = platforms?.map((item: any) => ({
      id: item._id,
      label: item.platformName,
    }));
    this.operatorOptions = operators?.map((item: any) => ({
      id: item._id,
      platform: item?.platform,
      label: item.operatorName,
    }));
    this.brandOptions = brands?.map((item: any) => ({
      id: item._id,
      platform: item?.platform,
      operator: item?.operator,
      label: item.brandName,
    }));

    this.rewardPacks = rewardPacks || [];
    this.rewardPackOptions = this.transformRewardPacks(rewardPacks);
  }

  onSignOut() {
    this.email = null;
    this.role = null;
    this.token = null;
  }

  setUserInfo() {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    this.token = userInfo?.token;
    this.email = userInfo?.email;
    this.role = userInfo?.role;
  }

  hasRole(requiredRole: any) {
    return Array.isArray(requiredRole)
      ? requiredRole.includes(this.role)
      : this.role === requiredRole;
  }

  isLoggedIn() {
    return Boolean(localStorage.getItem("userInfo"));
  }

  updatePBO(data: any, type: any) {
    let _data: any[] = [];
    if (!type && !data) return;

    if (type === "platform") {
      _data = [
        ...this.platformOptions,
        { id: data?._id, label: data?.platformName },
      ];
    } else if (type === "operator") {
      _data = [
        ...this.operatorOptions,
        {
          id: data?._id,
          platform: data?.platform,
          label: data?.operatorName,
        },
      ];
    } else if (type === "brand") {
      _data = [
        ...this.brandOptions,
        {
          id: data?._id,
          platform: data?.platform,
          operator: data?.operator,
          label: data?.brandName,
        },
      ];
    }

    this[`${type}Options`] = _data;
  }

  getFilteredOperators({ platformIds = [] }: any) {
    return this.allowedOperators
      .filter((operator) => {
        return platformIds.includes(operator.platform);
      })
      .map((operator) => {
        return {
          ...operator,
          id: operator._id,
          label: operator.operatorName,
        };
      });
  }

  getFilteredBrands({ platformIds = [], operatorIds = [] }: any) {
    return this.allowedBrands
      .filter((brand) => {
        return (
          platformIds.includes(brand.platform) &&
          operatorIds.includes(brand.operator)
        );
      })
      .map((brand) => {
        return {
          ...brand,
          id: brand._id,
          label: brand.brandName,
        };
      });
  }

  getCountryOptions() {
    return this.countries?.map((country: any) => ({
      id: country?.isoCode,
      label: country?.name,
    }));
  }

  getCurrencyOptions() {
    return generateOptions({
      items: this.currencies,
      id: "isoCode",
      label: "isoCode",
    } as any);
  }

  getCardBgOptions() {
    return this.cardBgs.map((el: any) => ({
      id: el?._id,
      label: el?.name,
    }));
  }

  getClubOptions() {
    return this.clubs.map((cl: any) => ({
      id: cl.externalClubId,
      label: `${cl.name} - ${cl.abbreviation}`,
    }));
  }

  // Add this helper method to transform reward packs into options format
  transformRewardPacks(rewardPacks: any) {
    return (rewardPacks || []).map((pack: any) => ({
      id: pack._id,
      label: `${pack.name} - ${pack.type}`, // Format similar to DailyRewardsEditor
    }));
  }

  // Add this method to update reward packs if needed
  updateRewardPacks(newPacks: any) {
    this.rewardPacks = newPacks || [];
    this.rewardPackOptions = this.transformRewardPacks(newPacks);
  }
}

const sessionHelperInstance = new SessionHelper();
export default sessionHelperInstance;
